import React from 'react';
// import {useTranslation} from 'react-i18next';
// import {isMobile} from 'react-device-detect';
import {getDate, getMonth, getYear, isValid} from 'date-fns';
// import i18n from 'i18n';
import {ErrorType} from '../../../utils/common';
import {MONTHS} from '../../../utils/constants'
import {DatepickerDateType, InputEventType, SelectOptionType} from '../../../utils/types';
import {device} from '../../../styled/device';
import {useScreenResize} from '../../../utils/hooks';
// import {useIpDetails} from 'context/ipDetails';
// import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
// import {COUNTRY_CODES} from 'utils/constants';
import {FilterOptionOption as Option} from 'react-select/dist/declarations/src/filters';
import {
  FieldQuestionMarkButton,
  TooltipTrigger,
} from '../FieldInput/styled';
import {
  DateInput,
  DateSelect,
  DateSelectWrapper,
  ErrorMessage,
  FieldsWrapper,
  Label,
  LabelWrapper,
  TooltipStyled,
  Wrapper,
} from './styled';

const DAY_PATTERN = /^(([0]?[1-9])|([1-2][0-9])|(3[01]))$/;
const YEAR_PATTERN = /^(19|20)\d{2}$/;


function getNextMonth(monthIndex = 0) {
  const monthNumber = monthIndex + 1;
  return Object.values(MONTHS()).find(m => m.value === String(monthNumber));
}

type DatePickerValue = {
  day?: string | number | null;
  month?: SelectOptionType | null;
  year?: string | number | null;
};

type DatepickerProps = {
  label?: string;
  defaultValues?: DatePickerValue;
  onChange?: (value: DatepickerDateType, name?: string) => void;
  invalid?: boolean;
  value?: DatepickerDateType;
  error?: ErrorType;
  disabled?: boolean;
  showDefaultMonth?: boolean;
  name?: string;
  withTooltip?: boolean;
  tooltipContentKey?: string | React.ReactNode;
  className?: string;
};

const Datepicker = React.forwardRef<HTMLInputElement, DatepickerProps>(
  (
    {
      label,
      defaultValues,
      onChange,
      invalid,
      value,
      error,
      disabled,
      name,
      withTooltip,
      tooltipContentKey,
      showDefaultMonth,
      className,
    },
    ref,
  ) => {
    const {isMatch} = useScreenResize(device.tablet);
    const isMobileMode = isMatch /*|| isMobile*/;
    const positionTooltip = isMobileMode ? 'top center' : 'top right';

    const getDefaultMonth = () => {
      if (defaultValues?.month) {
        return defaultValues.month;
      }
      if (showDefaultMonth) {
        return Object.values(MONTHS).find(option => {
          return option?.value === (today.getMonth() + 1).toString();
        });
      }
      return undefined;
    };
    const getDefaultYear = () => {
      if (defaultValues?.year) {
        return defaultValues.year;
      }
      if (showDefaultMonth) {
        return today.getFullYear();
      }
      return undefined;
    };

    // const {t} = useTranslation();
    const today = new Date();
    const [day, setDay] = React.useState(defaultValues?.day);
    const [month, setMonth] = React.useState<SelectOptionType | undefined | null>(
      getDefaultMonth(),
    );
    const [year, setYear] = React.useState(getDefaultYear());
    const [isInvalidDate, setIsInvalidDate] = React.useState(false);
    const [isYearInvalid, setIsYearInvalid] = React.useState(false);
    const stringValue = value?.toString();
    // const {isUsaReservation} = useComputedReservationDetails();
    // const {ipDetails} = useIpDetails();
    // const isMMddYYYYFormat =
    //   isUsaReservation || ipDetails?.country_code === COUNTRY_CODES.usa;

    React.useEffect(() => {
      function setValue(date: Date) {
        const isValidValueDate = isValid(date);
        if (isValidValueDate) {
          const day = getDate(date);
          setDay(String(day));

          const month = getMonth(date);
          setMonth(getNextMonth(month));

          const year = getYear(date);
          setYear(String(year));
        }
      }

      if (stringValue) {
        const date = new Date(stringValue);
        setValue(date);
      }
    }, [stringValue]);

    React.useEffect(
      function resetDateFields() {
        if (value === null) {
          setDay(undefined);
          setMonth(null);
          setYear(undefined);
        }
      },
      [value],
    );

    React.useEffect(
      function updateMonthTranslationOnLanguageChange() {
        setMonth(prevMonth => {
          if (!prevMonth) {
            return prevMonth;
          }

          const updatedTranslationMonth = Object.values(MONTHS()).find(month => {
            return month.value === prevMonth.value;
          });
          return updatedTranslationMonth;
        });
      },
      [],
    );

    const handleChange = React.useCallback(
      (params: DatePickerValue) => {
        const day = params?.day;
        const monthValue = params?.month?.value;
        const year = params?.year;

        if (!day || !monthValue || !year) {
          onChange!(undefined, name);
          return;
        }

        if (!YEAR_PATTERN.test(String(year))) {
          setIsYearInvalid(true);
          onChange!(undefined, name);
          return;
        } else {
          setIsYearInvalid(false);
        }

        const date = new Date(`${monthValue}/${day}/${year}`);
        if (isValid(date)) {
          setIsInvalidDate(false);
          onChange!(date, name);
        } else {
          setIsInvalidDate(true);
          onChange!(undefined, name);
        }
      },
      [name, onChange],
    );

    const handleDayChange = React.useCallback(
      ({target}: InputEventType) => {
        const value = target.value;
        if (value !== '' && !DAY_PATTERN.test(value)) {
          return;
        }

        setDay(value);
        handleChange({
          day: value,
          month,
          year,
        });
      },
      [handleChange, month, year],
    );

    const handleYearChange = React.useCallback(
      ({target}: InputEventType) => {
        const value = target.value;
        if (value.length > 4) {
          return;
        }

        setYear(value);
        handleChange({
          year: value,
          day,
          month,
        });
      },
      [day, handleChange, month],
    );

    const handleMonthChange = React.useCallback(
      (option: Option<any>) => {
        setMonth(option);
        handleChange({
          month: option,
          day,
          year,
        });
      },
      [day, handleChange, year],
    );

    return (
      <Wrapper aria-label={name} disabled={disabled} className={className}>
        <LabelWrapper>
          <Label>{label}</Label>
          {withTooltip && (
            <TooltipStyled position={positionTooltip} label={tooltipContentKey}>
              <TooltipTrigger>
                <FieldQuestionMarkButton />
              </TooltipTrigger>
            </TooltipStyled>
          )}
        </LabelWrapper>
        <FieldsWrapper>
          {/* {isMMddYYYYFormat ? (
            <>
              <DateSelectWrapper>
                <DateSelect
                  disabled={disabled}
                  options={Object.values(MONTHS())}
                  value={month}
                  onChange={handleMonthChange}
                  invalid={invalid || Boolean(error) || isInvalidDate}
                  name={`${name}-month`}
                  placeholder={t('month')}
                />
              </DateSelectWrapper>
              <DateInput
                disabled={disabled}
                onChange={handleDayChange}
                placeholder={t('day')}
                value={String(day || '')}
                type="number"
                name={`${name}-day`}
                invalid={invalid || Boolean(error) || isInvalidDate}
                ref={ref}
              />
            </>
          ) : ( */}{(
            <>
              <DateInput
                disabled={disabled}
                onChange={handleDayChange}
                placeholder={'day'}
                value={String(day || '')}
                type="number"
                name={`${name}-day`}
                inputMode="numeric"
                invalid={invalid || Boolean(error) || isInvalidDate}
                ref={ref}
              />
              <DateSelectWrapper>
                <DateSelect
                  disabled={disabled}
                  options={Object.values(MONTHS())}
                  value={month}
                  onChange={handleMonthChange}
                  invalid={invalid || Boolean(error) || isInvalidDate}
                  name={`${name}-month`}
                  placeholder={'month'}
                />
              </DateSelectWrapper>
            </>
          )}
          <DateInput
            disabled={disabled}
            onChange={handleYearChange}
            placeholder={'year'}
            value={String(year || '')}
            type="number"
            inputMode="numeric"
            name={`${name}-year`}
            invalid={invalid || Boolean(error) || isInvalidDate || isYearInvalid}
          />
        </FieldsWrapper>
        {error && <ErrorMessage data-testid={`${name}-error`}> error</ErrorMessage>}
      </Wrapper>
    );
  },
);

export {Datepicker};
